import { fromJS, Map, List, OrderedMap, Set } from 'immutable';
import Actions from 'flow/actions/';
import { sortStrings } from 'flow/utils/sorter-utils';
import {
    TICKET_KEYS_DATA_PATHS,
    eventIsKeptInStore,
} from 'flow/utils/ticket-utils';
import {
    keyIsTypeConversationMessage,
    keyIsTypeConversationMessagePlaceholder,
} from 'flow/utils/ticket-utils';
import { getTagsFromString, listToMap } from 'flow/utils';
import { eventIsCallRecordEvent } from 'flow/types/TicketEventData';
import {
    isIncludedInTicketsSortingDataInterface,
    sortingDataKeys,
} from 'flow/types/Tickets';

export const initialState = fromJS({
    [TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER]: Map(),
    [TICKET_KEYS_DATA_PATHS.UNASSIGNED]: Map(),
    [TICKET_KEYS_DATA_PATHS.CLOSED]: Map(),
    [TICKET_KEYS_DATA_PATHS.ALL_LIST]: Map(),
    [TICKET_KEYS_DATA_PATHS.RELATED]: Map(),
    tempSearchData: Map(),
    ticketsData: Map(),
    ticketEvents: Map(),
    metadata: Map(),
    componentsMountStatus: Map(),
    settings: Map(),
});

// Tab lists are NOT ordered here. The reducer keeps keys-data in a plain
// (key-addressed) Map; ordering is derived at read time by the memoized list
// selectors (Mine/Unassigned/All wrappers), which mirror the server sort. This
// keeps a single source of truth for order and avoids stale stored order on
// in-place updates.

const getTicketsKeysDataPaths = (BASE_PATH, EXTRA_PATH) => {
    return {
        keysPath: EXTRA_PATH
            ? [BASE_PATH, EXTRA_PATH, 'keys']
            : [BASE_PATH, 'keys'],
        nextPageNumberPath: EXTRA_PATH
            ? [BASE_PATH, EXTRA_PATH, 'nextPageNumber']
            : [BASE_PATH, 'nextPageNumber'],
        apiHasMoreDataPath: EXTRA_PATH
            ? [BASE_PATH, EXTRA_PATH, 'apiHasMoreData']
            : [BASE_PATH, 'apiHasMoreData'],
    };
};

const getTicketDataReducedForTicketsKeysPath = (ticketData) => {
    return sortingDataKeys.reduce((result, key) => {
        if (ticketData.has(key)) {
            return result.set(key, ticketData.get(key));
        }
        return result;
    }, Map());
};

const getSortingDataPerTabForTicket = (ticketKey) => {
    return [
        [TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys', ticketKey],
        [TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey],
        [TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey],
        [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys', ticketKey],
    ];
};

const getFullTicketDataPathsForTicket = (ticketKey) => {
    return [
        ['ticketsData', 'byKey', ticketKey],
        ['tempSearchData', 'byKey', ticketKey],
    ];
};

const getStateWithTicketPropertyUpdated = (
    state,
    ticketKey,
    propertyName,
    value,
    shouldReplace,
) => {
    const basePaths = isIncludedInTicketsSortingDataInterface(propertyName)
        ? getFullTicketDataPathsForTicket(ticketKey).concat(
              getSortingDataPerTabForTicket(ticketKey),
          )
        : getFullTicketDataPathsForTicket(ticketKey);
    // Above: if propertyName is part of the reduced ticket data interface (SortingData),
    // we include the paths holding this data for the reduce below, where
    // we update the value in respective path where the ticket exists
    // (as long as the new value is not identical to the one in the store,
    // and the optional shouldReplace(currentValue, value) predicate allows it)

    return basePaths.reduce((currentState, basePath) => {
        if (!currentState.hasIn(basePath)) return currentState; //ticket does not exist in store, early return
        const propertyPath = [...basePath, propertyName];
        const currentValue = currentState.getIn(propertyPath);
        if (currentValue === value) return currentState; //new value identical to existing value, early return
        if (shouldReplace && !shouldReplace(currentValue, value))
            return currentState;
        return currentState.setIn(propertyPath, value);
    }, state);
};

// Timestamps may arrive out of order (websocket events racing each other and
// fetches), so sort keys must only ever move forward. Compared as dates: the
// push payload and the REST DTO are not guaranteed to format identically.
const isNewerTimestamp = (currentValue, value) =>
    !currentValue || new Date(value) > new Date(currentValue);

/**
 * Members arrive from the API as an array, so `fromJS` yields a List. Store a Set
 * instead: membership has no order (the Access dialog sorts by name at render) and
 * no duplicates, and the lookup is flat rather than a scan — which matters because
 * a ticket list checks membership once per visible row on every store update.
 */
const withMembersAsSet = (ticket) => {
    const members = ticket.get('members');
    return members ? ticket.set('members', members.toSet()) : ticket;
};

const getStateUpdatedForTicketsSearched = (
    state,
    ticketsReceived,
    nextPageNumber,
    apiHasMoreData,
    overwriteExistingTempSearchData,
) => {
    const ticketsByKeyReceived = ticketsReceived.reduce(
        (result, item) => result.set(item.get('key'), item),
        OrderedMap(), //<-- This here important
        // If using standard Immutable JS Map(), order of the list is not preserved,
        // and for search results we want to use whatever order the backend algorithm has deemed is appropriate
    );

    let stateToReturn = state;
    ticketsByKeyReceived.forEach((ticket) => {
        const tags = ticket.get('tags');
        if (!tags || tags.size === 0) return;
        const tagsMap = listToMap(tags, (tag) => tag.get('key'));
        stateToReturn = stateToReturn.setIn(
            ['metadata', 'perTicket', ticket.get('key'), 'tagsData'],
            tagsMap,
        );
    });

    const ticketsByKeyReceivedWithTagsRemovedFromDTOs =
        ticketsByKeyReceived.map((ticket) =>
            withMembersAsSet(ticket.delete('tags')),
        );

    if (overwriteExistingTempSearchData) {
        return stateToReturn
            .setIn(
                ['tempSearchData', 'byKey'],
                ticketsByKeyReceivedWithTagsRemovedFromDTOs,
            )
            .setIn(['tempSearchData', 'nextPageNumber'], nextPageNumber)
            .setIn(['tempSearchData', 'apiHasMoreData'], apiHasMoreData);
    } else {
        return stateToReturn
            .mergeIn(
                ['tempSearchData', 'byKey'],
                ticketsByKeyReceivedWithTagsRemovedFromDTOs,
            )
            .setIn(['tempSearchData', 'nextPageNumber'], nextPageNumber)
            .setIn(['tempSearchData', 'apiHasMoreData'], apiHasMoreData);
    }
};

const getStateUpdatedWithTicketsDataReceived = (
    state,
    ticketsReceived,
    nextPageNumberReceived,
    apiHasMoreDataReceived,
    TICKETS_KEYS_DATA_BASE_PATH,
    EXTRA_PATH,
    shouldAddToAllPath,
) => {
    const ticketsByKeyReceived = listToMap(ticketsReceived, (ticket) =>
        ticket.get('key'),
    );

    const ticketsKeysDataMapReceived = ticketsByKeyReceived.map(
        getTicketDataReducedForTicketsKeysPath,
    );

    const KEYS_DATA_PATHS = getTicketsKeysDataPaths(
        TICKETS_KEYS_DATA_BASE_PATH,
        EXTRA_PATH,
    );

    const ticketsKeysData = state
        .getIn(KEYS_DATA_PATHS.keysPath, Map())
        .merge(ticketsKeysDataMapReceived);

    const nextPageNumber =
        nextPageNumberReceived ||
        state.getIn(KEYS_DATA_PATHS.nextPageNumberPath);
    const apiHasMoreData =
        apiHasMoreDataReceived !== undefined
            ? apiHasMoreDataReceived
            : state.getIn(KEYS_DATA_PATHS.apiHasMoreDataPath);

    let stateToReturn = state;
    ticketsByKeyReceived.forEach((ticket) => {
        const tags = ticket.get('tags');
        if (!tags || tags.size === 0) return;
        const tagsMap = listToMap(tags, (tag) => tag.get('key'));
        stateToReturn = stateToReturn.setIn(
            ['metadata', 'perTicket', ticket.get('key'), 'tagsData'],
            tagsMap,
        );
    });

    const ticketsByKeyReceivedWithTagsRemovedFromDTOs =
        ticketsByKeyReceived.map((ticket) =>
            withMembersAsSet(ticket.delete('tags')),
        );

    stateToReturn = stateToReturn
        .mergeIn(
            ['ticketsData', 'byKey'],
            ticketsByKeyReceivedWithTagsRemovedFromDTOs,
        )
        .setIn(KEYS_DATA_PATHS.keysPath, ticketsKeysData)
        .setIn(KEYS_DATA_PATHS.nextPageNumberPath, nextPageNumber)
        .setIn(KEYS_DATA_PATHS.apiHasMoreDataPath, apiHasMoreData);

    if (
        !shouldAddToAllPath ||
        TICKETS_KEYS_DATA_BASE_PATH === TICKET_KEYS_DATA_PATHS.ALL_LIST
    )
        return stateToReturn;

    return stateToReturn.mergeIn(
        [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'],
        ticketsKeysDataMapReceived,
    );
};

const getStateWithTicketKeysDataPathsUpdated = (
    state,
    ticketKey,
    ticketState,
    assignee,
    ownExtensionKey,
) => {
    const ticketData = state.getIn(['ticketsData', 'byKey', ticketKey], Map());
    const ticketKeysData = getTicketDataReducedForTicketsKeysPath(ticketData);

    /**
     * A few words about this method and the state it returns:
     *
     * This method is used to update the keys data paths when the assignee or state (open/closed) of a ticket is changed.
     * The keys data paths hold which tickets to display in which tickets list (mine/unassigned/all); the order they
     * appear in is derived at read time by the list selectors, not stored here.
     *
     * So:
     * When a ticket goes from state X to state Y, we remove the ticketKeys data from path X and add it to path Y
     * Because of this there's a small risk a scroll to the bottom of respective tickets list might retrieve data
     * already available in the store.
     * Both the risk and impact of this are small enough for it to be worth doing the state juggling we do here, imo.
     * Alternatively, we could settle with only removing the keys data from the path it no longer belongs in,
     * and not adding it to its new proper path...
     * (...at the cost of the user not instantly seeing the ticket in the relevant path should he/she be in that tab
     * when the status/assignee is set)
     *
     * More specifically what can occur (given my thinking is right):
     * Say we have 25 tickets open assigned to logged-in user. Now assume 25 (or more) older tickets with other states
     * are opened and assigned to logged-in user. The data is pushed to us from Top, and we now have 50 tickets
     * in path ASSIGNED_LOGGED_IN_USER, but the same nextPageNumber value as before. Scrolling to the bottom of the list
     * displaying open tickets assigned to logged-in user will then fetch the same (or partially the same) tickets as
     * the last 25 already available in the state.
     */

    const ticketKeysDataAllList = state
        .getIn([TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'], Map())
        .set(ticketKey, ticketKeysData);

    if (ticketState === 'open' && assignee === ownExtensionKey) {
        const ticketKeysDataOpenAssignedLoggedInUser = state
            .getIn(
                [TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys'],
                Map(),
            )
            .set(ticketKey, ticketKeysData);

        return state
            .setIn(
                [TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER, 'keys'],
                ticketKeysDataOpenAssignedLoggedInUser,
            )
            .setIn(
                [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'],
                ticketKeysDataAllList,
            )
            .deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey])
            .deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
    } else if (
        ticketState === 'open' &&
        (!assignee || assignee === 'unassigned')
    ) {
        const ticketKeysDataOpenUnassigned = state
            .getIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys'], Map())
            .set(ticketKey, ticketKeysData);
        return state
            .setIn(
                [TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys'],
                ticketKeysDataOpenUnassigned,
            )
            .setIn(
                [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'],
                ticketKeysDataAllList,
            )
            .deleteIn([
                TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
                'keys',
                ticketKey,
            ])
            .deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
    } else if (
        ticketState === 'open' &&
        assignee &&
        assignee !== ownExtensionKey
    ) {
        return state
            .setIn(
                [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'],
                ticketKeysDataAllList,
            )
            .deleteIn([
                TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
                'keys',
                ticketKey,
            ])
            .deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey])
            .deleteIn([TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey]);
    } else if (ticketState === 'closed') {
        return state
            .setIn(
                [TICKET_KEYS_DATA_PATHS.CLOSED, 'keys', ticketKey],
                ticketKeysData,
            )
            .setIn(
                [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys', ticketKey],
                ticketKeysData,
            )
            .deleteIn([
                TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
                'keys',
                ticketKey,
            ])
            .deleteIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys', ticketKey]);
    } else {
        //Shouldn't happen..
        return state.setIn(
            [TICKET_KEYS_DATA_PATHS.ALL_LIST, 'keys'],
            ticketKeysDataAllList,
        );
    }
};
// eslint-disable-next-line complexity
export const ticketsReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }

        case Actions.CLEAR_TICKETS_DATA: {
            const componentsMountStatus = state.get('componentsMountStatus');
            const settings = state.get('settings');
            return initialState
                .set('componentsMountStatus', componentsMountStatus)
                .set('settings', settings);
        }

        case Actions.SET_TICKETS_RELATED_DATA: {
            const ticketsList = fromJS(action.data);
            const ticketKey = fromJS(action.ticketKey);

            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                TICKET_KEYS_DATA_PATHS.RELATED,
                ticketKey,
            );
        }
        case Actions.SET_OPEN_TICKETS_ASSIGNED_USER_DATA: {
            const ticketsList = fromJS(action.data);

            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
            );
        }
        case Actions.SET_OPEN_TICKETS_UNASSIGNED_DATA: {
            const ticketsList = fromJS(action.data);

            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                TICKET_KEYS_DATA_PATHS.UNASSIGNED,
            );
        }
        case Actions.SET_CLOSED_TICKETS_DATA: {
            const ticketsList = fromJS(action.data);

            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                TICKET_KEYS_DATA_PATHS.CLOSED,
            );
        }
        case Actions.SET_TICKETS_ALL_DATA: {
            const ticketsList = fromJS(action.data);

            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                TICKET_KEYS_DATA_PATHS.ALL_LIST,
            );
        }
        case Actions.SET_TICKETS_SEARCHED_DATA: {
            const ticketsList = fromJS(action.data);

            return getStateUpdatedForTicketsSearched(
                state,
                ticketsList,
                action.nextPageNumber,
                action.apiHasMoreData,
                action.nextPageNumber === undefined,
            );
        }
        case Actions.SET_TICKET_DATA: {
            const ticket = fromJS(action.data);
            const ticketState = ticket.get('state');
            const assignee = ticket.get('assignee');
            const ownExtensionKey = action.ownExtensionKey;

            let ticketKeysDataPath;

            if (ticketState === 'open' && assignee === ownExtensionKey) {
                ticketKeysDataPath =
                    TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER;
            } else if (
                ticketState === 'open' &&
                (!assignee || assignee === 'unassigned')
            ) {
                ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.UNASSIGNED;
            } else if (
                ticketState === 'open' &&
                assignee &&
                assignee !== ownExtensionKey
            ) {
                ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.ALL_LIST;
            } else if (ticketState === 'closed') {
                ticketKeysDataPath = TICKET_KEYS_DATA_PATHS.CLOSED;
            }

            const ticketInList = List([ticket]);
            return getStateUpdatedWithTicketsDataReceived(
                state,
                ticketInList,
                undefined,
                undefined,
                ticketKeysDataPath,
                undefined,
                true,
            );
        }
        case Actions.SET_TICKET_EVENTS: {
            let dynamicData = Map({});
            // Events the client can show neither as themselves nor as unsupported
            // content are not stored, so no selector or date section sees them.
            const eventsToStore = action.data.filter((event) =>
                eventIsKeptInStore(
                    event.DTOSubtype,
                    event.message,
                    event.attachments?.length,
                ),
            );
            const ticketEventsData = fromJS(
                eventsToStore.map(({ status, attachments, ...event }) => {
                    if (status) {
                        dynamicData = dynamicData.setIn(
                            [event.key, 'deliveryStatus'],
                            status,
                        );
                    }
                    if (attachments && attachments.length > 0) {
                        dynamicData = dynamicData.setIn(
                            [event.key, 'attachments'],
                            fromJS(attachments),
                        );
                    }
                    if (eventIsCallRecordEvent(event.DTOSubtype)) {
                        // The CallRecord payload (`callRecord` on the DTO) is upserted into the
                        // canonical call-records store on ingest, so keep only what the feed/card
                        // needs here — the rest would be a duplicated, drift-prone copy.
                        return {
                            DTOSubtype: event.DTOSubtype,
                            key: event.key,
                            timeStamp: event.timeStamp,
                            callRecordKey: event.callRecord?.key,
                        };
                    }
                    return event;
                }),
            );

            const ticketEventsDataMap = listToMap(ticketEventsData, (event) =>
                event.get('key'),
            );

            const ticketKey = action.ticketKey;
            return state
                .setIn(
                    ['ticketEvents', 'dataStatic', ticketKey],
                    ticketEventsDataMap,
                )
                .withMutations((ticketsState) => {
                    updateEventDataDynamicState(
                        ticketsState,
                        ticketKey,
                        dynamicData,
                    );
                });
        }
        case Actions.SET_TICKET_EVENT_ATTACHMENTS: {
            const ticketKey = action.ticketKey;
            const eventKey = action.eventKey;
            const attachments = fromJS(action.data);

            const eventExistsInStore = state.hasIn([
                'ticketEvents',
                'dataStatic',
                ticketKey,
                eventKey,
            ]);

            if (!eventExistsInStore) return state;

            return state.setIn(
                [
                    'ticketEvents',
                    'dataDynamic',
                    ticketKey,
                    eventKey,
                    'attachments',
                ],
                attachments,
            );
        }
        case Actions.SET_TICKET_EVENT_MESSAGE_STATUS: {
            const ticketKey = action.ticketKey;
            const messageKey = action.messageKey;

            const eventExistsInStore = state.hasIn([
                'ticketEvents',
                'dataStatic',
                ticketKey,
                messageKey,
            ]);

            if (!eventExistsInStore) return state;

            return state.setIn(
                [
                    'ticketEvents',
                    'dataDynamic',
                    ticketKey,
                    messageKey,
                    'deliveryStatus',
                ],
                action.messageStatus,
            );
        }
        case Actions.UPDATE_TICKET: {
            const ticketKey = action.ticketKey;
            const ticketInStore = state.getIn([
                'ticketsData',
                'byKey',
                ticketKey,
            ]);
            if (!ticketInStore) {
                return state;
            }

            return state.setIn(
                ['ticketsData', 'byKey', ticketKey],
                withMembersAsSet(fromJS(action.ticketDTO)),
            );
        }
        case Actions.UPDATE_TICKET_TEMP_SEARCH_DATA: {
            const ticketKey = action.ticketKey;
            const ticketInStore = state.getIn([
                'tempSearchData',
                'byKey',
                ticketKey,
            ]);
            if (!ticketInStore) return state;

            return state.setIn(
                ['tempSearchData', 'byKey', ticketKey],
                withMembersAsSet(fromJS(action.ticketDTO)),
            );
        }
        case Actions.ADD_TICKET_EVENT: {
            const data = fromJS(action.eventData);
            const ticketKey = action.ticketKey;
            const actorIsOwnExtension = action.actorIsOwnExtension;
            const dataSource = action.dataSource;

            const eventKey = data.get('key');
            const attachments = data.get('attachments');
            const deliveryStatus = data.get('status');

            if (
                !eventIsKeptInStore(
                    data.get('DTOSubtype'),
                    data.get('message'),
                    attachments?.size,
                )
            ) {
                return state;
            }

            if (
                actorIsOwnExtension &&
                keyIsTypeConversationMessage(eventKey) &&
                dataSource === 'TOP'
            ) {
                const placeholderMessageEventExists = state
                    .getIn(['ticketEvents', 'dataStatic', ticketKey], Map())
                    .valueSeq()
                    .some((event) =>
                        keyIsTypeConversationMessagePlaceholder(
                            event.get('key'),
                        ),
                    );

                if (placeholderMessageEventExists) return state;
                // About block above: we check if it's a message sent by logged-in user, and if the data
                // is received from Top. If it is, we check if there are placeholder events
                // in the store, which is the case if the message has been sent from the same client.
                // When this is true, we do an early exit, returning state as is, as we leave the data handling
                // to the API callback (where dataSource will not be set to 'TOP').

                // Without this block, we'd have duplicate events in the case
                // the Top event reaches the client before the API callback does
                // (which does indeed occur at times).
            }

            const ticketEventDataStatic = data
                .delete('status')
                .delete('attachments'); //Setting status and attachments in its own path

            const dynamicData = fromJS({
                [eventKey]: {
                    ...(attachments && { attachments }),
                    ...(deliveryStatus && { deliveryStatus }),
                },
            });

            return state
                .mergeIn(
                    ['ticketEvents', 'dataStatic', ticketKey, eventKey],
                    ticketEventDataStatic,
                )
                .withMutations((ticketsState) => {
                    updateEventDataDynamicState(
                        ticketsState,
                        ticketKey,
                        dynamicData,
                    );
                });
        }

        case Actions.SET_TICKET_EVENT: {
            const ticketKey = action.ticketKey;
            const placeholderKeyToRemove = action.placeholderKeyToRemove;

            const attachments = action.eventData.attachments;
            const deliveryStatus = action.eventData.status;

            const dynamicData = fromJS({
                [action.eventData.key]: {
                    ...(attachments && { attachments }),
                    ...(deliveryStatus && { deliveryStatus }),
                },
            });

            const dataStatic = fromJS(action.eventData)
                .delete('status')
                .delete('attachments');

            return state.withMutations((ticketsState) => {
                if (placeholderKeyToRemove) {
                    ticketsState
                        .deleteIn([
                            'ticketEvents',
                            'dataStatic',
                            ticketKey,
                            placeholderKeyToRemove,
                        ])
                        .deleteIn([
                            'ticketEvents',
                            'dataDynamic',
                            ticketKey,
                            placeholderKeyToRemove,
                        ]);
                }
                ticketsState.setIn(
                    [
                        'ticketEvents',
                        'dataStatic',
                        ticketKey,
                        action.eventData.key,
                    ],
                    dataStatic,
                );
                updateEventDataDynamicState(
                    ticketsState,
                    ticketKey,
                    dynamicData,
                );
            });
        }
        case Actions.UPLOAD_FILE_STAGE_IN_SEND_TICKET_MESSAGE_FINISHED: {
            const attachmentData = action.attachmentData;
            if (!attachmentData) return state;
            return state.setIn(
                [
                    'ticketEvents',
                    'dataDynamic',
                    action.ticketKey,
                    action.placeholderKey,
                    'attachments',
                ],
                fromJS([attachmentData]),
            );
        }
        case Actions.SET_TICKET_ASSIGNEE: {
            const ticketKey = action.ticketKey;
            const newAssignee = action.assigneeKey;
            const ownExtensionKey = action.ownExtensionKey;

            const ticketInStore = state.getIn([
                'ticketsData',
                'byKey',
                ticketKey,
            ]);
            if (!ticketInStore) {
                return state;
            }

            const ticketState = ticketInStore.get('state');

            //TO DO: when lastUpdate becomes relevant, look into updating that value
            const stateWithTicketKeysPathsDataUpdated =
                getStateWithTicketKeysDataPathsUpdated(
                    state,
                    ticketKey,
                    ticketState,
                    newAssignee,
                    ownExtensionKey,
                );
            return stateWithTicketKeysPathsDataUpdated.setIn(
                ['ticketsData', 'byKey', ticketKey, 'assignee'],
                newAssignee,
            );
        }
        case Actions.SET_TICKET_STATE: {
            const ticketKey = action.ticketKey;
            const newTicketState = action.ticketState;
            const ownExtensionKey = action.ownExtensionKey;

            const ticketInStore = state.getIn([
                'ticketsData',
                'byKey',
                ticketKey,
            ]);
            if (!ticketInStore) {
                return state;
            }

            const assignee = ticketInStore.get('assignee');

            //TO DO: when lastUpdate becomes relevant, look into updating that value
            const stateWithTicketKeysPathsDataUpdated =
                getStateWithTicketKeysDataPathsUpdated(
                    state,
                    ticketKey,
                    newTicketState,
                    assignee,
                    ownExtensionKey,
                );
            return stateWithTicketKeysPathsDataUpdated.setIn(
                ['ticketsData', 'byKey', ticketKey, 'state'],
                newTicketState,
            );
        }
        case Actions.SET_TICKET_AWAITING_ACTIVITY: {
            return getStateWithTicketPropertyUpdated(
                state,
                action.ticketKey,
                'awaitingActivity',
                action.value,
            );
        }
        case Actions.SET_TICKET_LAST_UPDATED: {
            return getStateWithTicketPropertyUpdated(
                state,
                action.ticketKey,
                'lastUpdate',
                action.value,
                isNewerTimestamp,
            );
        }
        case Actions.SET_TICKET_SUBJECT: {
            return getStateWithTicketPropertyUpdated(
                state,
                action.ticketKey,
                'subject',
                action.value,
            );
        }
        case Actions.SET_TICKET_VISIBILITY: {
            return getStateWithTicketPropertyUpdated(
                state,
                action.ticketKey,
                'visibility',
                action.value,
            );
        }
        case Actions.SET_TICKET_EVENT_SUMMARY: {
            const data = fromJS(action.data).set('source', action.source);
            return state.setIn(
                ['metadata', action.eventKey, 'summaryData'],
                data,
            );
        }
        case Actions.SET_ALL_TAGS_AVAILABLE_FOR_TICKET_TAGGING: {
            const tagsData = listToMap(fromJS(action.data), (tag) =>
                tag.get('key'),
            );

            return state.setIn(['metadata', 'allTags'], tagsData);
        }
        case Actions.SET_NEW_TAG_CREATED: {
            const tagData = fromJS(action.tagData);
            const tagKey = tagData.get('key');
            return state.setIn(['metadata', 'allTags', tagKey], tagData);
        }
        case Actions.SET_TAGS_TO_TICKET: {
            const tagsData = listToMap(fromJS(action.data), (tag) =>
                tag.get('key'),
            ).sortBy((tag) => tag.get('label'), sortStrings);

            return state.setIn(
                ['metadata', 'perTicket', action.ticketKey, 'tagsData'],
                tagsData,
            );
        }
        case Actions.ATTACH_TAG_TO_TICKET: {
            const tagData = fromJS(action.data);
            const tagKey = tagData.get('key');
            const tagsDataUpdatedAndSorted = state
                .getIn(
                    ['metadata', 'perTicket', action.ticketKey, 'tagsData'],
                    OrderedMap(),
                )
                .set(tagKey, tagData)
                .sortBy((tag) => tag.get('label'), sortStrings);
            return state.setIn(
                ['metadata', 'perTicket', action.ticketKey, 'tagsData'],
                tagsDataUpdatedAndSorted,
            );
        }
        case Actions.REMOVE_TAG_FROM_TICKET: {
            return state.deleteIn([
                'metadata',
                'perTicket',
                action.ticketKey,
                'tagsData',
                action.tagKey,
            ]);
        }
        case Actions.SET_RENAME_TAG: {
            return state.withMutations((map) => {
                map.getIn(['metadata', 'perTicket'], Map()).forEach(
                    (metadataData, ticketKey) => {
                        const tagData = metadataData.getIn([
                            'tagsData',
                            action.tagKey,
                        ]);
                        if (tagData) {
                            map.setIn(
                                [
                                    'metadata',
                                    'perTicket',
                                    ticketKey,
                                    'tagsData',
                                    action.tagKey,
                                    'label',
                                ],
                                action.newName,
                            );
                        }
                    },
                );
                if (map.getIn(['metadata', 'allTags', action.tagKey])) {
                    map.setIn(
                        ['metadata', 'allTags', action.tagKey, 'label'],
                        action.newName,
                    );
                }
            });
        }
        case Actions.DELETE_TAGS_COMPLETELY: {
            const keysForTagsToDelete = action.tagKeys;
            return state.withMutations((map) => {
                map.getIn(['metadata', 'perTicket'], Map()).forEach(
                    (metadataData, ticketKey) => {
                        const tagsData = metadataData.get('tagsData', Map());
                        const updatedTagsData = tagsData.filter((tag) => {
                            const tagKey = tag.get('key');
                            return !keysForTagsToDelete.includes(tagKey);
                        });
                        map.setIn(
                            ['metadata', 'perTicket', ticketKey, 'tagsData'],
                            updatedTagsData,
                        );
                    },
                );
                keysForTagsToDelete.forEach((tagKey) => {
                    map.deleteIn(['metadata', 'allTags', tagKey]);
                });
            });
        }
        case Actions.SET_TICKET_OPEN_AI_EXPERIMENTAL_TAGS: {
            const tagsData = fromJS(action.data);
            const tagsString = tagsData.get('text');

            if (!tagsString) return state;

            const tags = getTagsFromString(tagsString);
            const tagsMap = Map(tags.map((tag) => [tag, tag]));
            const tagsDataUpdated = tagsData
                .set('tags', tagsMap)
                .set('source', action.source);

            return state.setIn(
                [
                    'metadata',
                    'perTicket',
                    action.ticketKey,
                    'openAiExperimentalTagsData',
                ],
                tagsDataUpdated,
            );
        }
        case Actions.REMOVE_TICKET_OPEN_AI_EXPERIMENTAL_TAG: {
            return state.deleteIn([
                'metadata',
                'perTicket',
                action.ticketKey,
                'openAiExperimentalTagsData',
                'tags',
                action.tag,
            ]);
        }
        case Actions.APPLY_TICKET_MEMBERS_CHANGE: {
            const ticketKey = action.ticketKey;
            const ticketInStore = state.getIn([
                'ticketsData',
                'byKey',
                ticketKey,
            ]);
            if (!ticketInStore) {
                return state;
            }

            const membersRemoved = action.membersRemoved || [];
            const membersAdded = action.membersAdded || [];

            // Set algebra dedupes on its own, so re-adding an existing member is a
            // no-op rather than something to filter out by hand.
            const membersUpdated = ticketInStore
                .get('members', Set())
                .subtract(membersRemoved)
                .union(membersAdded);

            return state.setIn(
                ['ticketsData', 'byKey', ticketKey, 'members'],
                membersUpdated,
            );
        }
        case Actions.REMOVE_TICKET: {
            const ticketKey = action.ticketKey;

            // Everything the store holds about a ticket the viewer may no longer
            // read. The full-data paths come from the helper rather than being
            // listed again here: tempSearchData is one of them, and selectTicket
            // falls back to it, so a ticket left there survives its own removal.
            const stateWithoutTicket = [
                ...getFullTicketDataPathsForTicket(ticketKey),
                ...getSortingDataPerTabForTicket(ticketKey),
                [TICKET_KEYS_DATA_PATHS.RELATED, ticketKey],
                ['ticketEvents', 'dataStatic', ticketKey],
                ['ticketEvents', 'dataDynamic', ticketKey],
                ['metadata', 'perTicket', ticketKey],
            ].reduce((stateSoFar, path) => stateSoFar.deleteIn(path), state);

            // It can also sit in another ticket's related list, where it would
            // render a row with nothing behind it.
            return stateWithoutTicket.updateIn(
                [TICKET_KEYS_DATA_PATHS.RELATED],
                Map(),
                (relatedPerTicket) =>
                    relatedPerTicket.map((relatedData) =>
                        relatedData.deleteIn(['keys', ticketKey]),
                    ),
            );
        }
        case Actions.REMOVE_TICKET_EVENT: {
            return state
                .deleteIn([
                    'ticketEvents',
                    'dataStatic',
                    action.ticketKey,
                    action.eventKey,
                ])
                .deleteIn([
                    'ticketEvents',
                    'dataDynamic',
                    action.ticketKey,
                    action.eventKey,
                ]);
        }
        case Actions.SET_TICKETS_BADGE_COUNT: {
            const count = action.count;
            return state.setIn(['ticketsData', 'ticketsBadgeCount'], count);
        }
        case Actions.SET_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL: {
            const count = action.count;
            return state.setIn(
                ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
                count,
            );
        }
        case Actions.INCREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL: {
            const currentCount = state.getIn(
                ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
                0,
            );
            const newCount = currentCount + 1;
            return state.setIn(
                ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
                newCount,
            );
        }
        case Actions.DECREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL: {
            const currentCount = state.getIn(
                ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
                0,
            );
            const newCount = currentCount - 1;
            return state.setIn(
                ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
                newCount,
            );
        }
        case Actions.SET_TICKETS_LIST_MOUNT_STATUS: {
            const isMounted = action.isMounted;
            const tabSelected = action.tabSelected;

            if (isMounted) {
                return state
                    .setIn(
                        ['componentsMountStatus', 'ticketsList', 'isMounted'],
                        true,
                    )
                    .setIn(
                        ['componentsMountStatus', 'ticketsList', 'tabSelected'],
                        tabSelected,
                    );
            } else {
                return state.deleteIn(['componentsMountStatus', 'ticketsList']);
            }
        }
        case Actions.SET_TICKET_VIEW_MOUNT_STATUS: {
            const isMounted = action.isMounted;
            const ticketKey = action.ticketKey;

            if (isMounted) {
                return state.setIn(
                    ['componentsMountStatus', 'ticketView', ticketKey],
                    fromJS({ context: action.context, ticketKey }),
                );
            } else {
                return state.deleteIn([
                    'componentsMountStatus',
                    'ticketView',
                    ticketKey,
                ]);
            }
        }
        case Actions.SET_TICKET_EVENTS_MOUNT_STATUS: {
            if (action.isMounted) {
                return state.setIn(
                    ['componentsMountStatus', 'ticketEvents', action.ticketKey],
                    true,
                );
            } else {
                return state.deleteIn([
                    'componentsMountStatus',
                    'ticketEvents',
                    action.ticketKey,
                ]);
            }
        }
        case Actions.SET_TICKETS_SETTINGS_FOR_QUEUE: {
            const ticketsSettingsDTO = fromJS(action.data);
            return state.setIn(
                ['settings', 'perQueue', action.queueEntityKey],
                ticketsSettingsDTO,
            );
        }
        case Actions.SET_TICKET_KEY_FOR_LOCUTUS_SESSION: {
            // A queue call's ticket can be created after its leg already rings.
            // The leg then never carries the ticket id in its bag variables, so
            // the Mine list joins the two on the locutus session id instead.
            return state.setIn(
                ['ticketKeysByLocutusSessionId', action.locutusSessionId],
                action.ticketKey,
            );
        }
        case Actions.SET_DEFAULT_TICKET_VISIBILITY_FOR_QUEUE: {
            const defaultTicketVisibilityDTO = fromJS(action.data);
            return state.setIn(
                [
                    'settings',
                    'defaultVisibilityPerQueue',
                    action.queueEntityKey,
                ],
                defaultTicketVisibilityDTO,
            );
        }
        case Actions.SET_TICKETS_TAB_ENFORCED: {
            return state.setIn(
                ['settings', 'ticketsTabIsEnforced'],
                action.value,
            );
        }
        case Actions.SET_EMAIL_SIGNATURES_ENABLED: {
            return state.setIn(
                ['settings', 'emailSignaturesEnabled'],
                action.value,
            );
        }
        case Actions.SET_TOTAL_RELATED_TICKETS_COUNT: {
            const count = action.count;
            return state.setIn(
                [
                    'ticketsData',
                    'totalNumberOfRelatedTickets',
                    action.ticketKey,
                ],
                count,
            );
        }
        case Actions.SET_TICKET_ATTACHMENT_SEND_RESTRICTIONS: {
            const restrictions = listToMap(fromJS(action.data), (restriction) =>
                restriction.get('channel'),
            );
            return state.setIn(['settings', 'attachments'], restrictions);
        }
        case Actions.SET_TICKET_REPLY_DRAFT: {
            const ticketKey = action.ticketKey;
            const replyType = action.replyType;

            if (!action.text) {
                const otherReplyType =
                    replyType === 'internal' ? 'external' : 'internal';
                const otherDraft = state.getIn([
                    'drafts',
                    ticketKey,
                    otherReplyType,
                ]);

                // If the other reply type is also empty/missing, delete the entire ticket key
                if (!otherDraft || otherDraft.isEmpty()) {
                    return state.deleteIn(['drafts', ticketKey]);
                }

                // Otherwise, just delete this reply type
                return state.deleteIn(['drafts', ticketKey, replyType]);
            }

            return state
                .setIn(['drafts', ticketKey, replyType, 'text'], action.text)
                .setIn(
                    ['drafts', ticketKey, replyType, 'mentions'],
                    action.mentions,
                );
        }
        case Actions.RESULT_UPDATED_CONTACT: {
            const contact = fromJS(action.data);
            const contactKey = contact.get('key');
            if (!contactKey) return state;

            const updateByKey = (byKey) =>
                byKey?.map((ticket) =>
                    ticket.getIn(['visitorDetails', 'key']) === contactKey
                        ? ticket.set('visitorDetails', contact)
                        : ticket,
                );

            return state
                .updateIn(['ticketsData', 'byKey'], updateByKey)
                .updateIn(['tempSearchData', 'byKey'], updateByKey);
        }
        default: {
            return state;
        }
    }
};

export default ticketsReducer;

const updateEventDataDynamicState = (
    wholeTicketState,
    ticketKey,
    dynamicDataByEventKey,
) => {
    dynamicDataByEventKey.forEach((data, eventKey) => {
        if (data.get('deliveryStatus')) {
            wholeTicketState.setIn(
                [
                    'ticketEvents',
                    'dataDynamic',
                    ticketKey,
                    eventKey,
                    'deliveryStatus',
                ],
                data.get('deliveryStatus'),
            );
        }
        if (data.get('attachments')) {
            wholeTicketState.setIn(
                [
                    'ticketEvents',
                    'dataDynamic',
                    ticketKey,
                    eventKey,
                    'attachments',
                ],
                data.get('attachments'),
            );
        }
    });
};
